import React from "react";
import { View, KeyboardAvoidingView, FlatList, Dimensions } from "react-native";

import Utils, {
  hasItems,
  isAndroid,
  escapeRegexp,
  navigate,
} from "../../../helpers/utils";
import appStyles from "../../appStyles";

import Search from "../common/search-bar/Search";
import Header from "../common/header/index";

import RuleWrapper from "./details/RuleWrapper";
import IntegrationsHelper from "./IntegrationsHelper";
import IntegrationsBlackListedFabButton from "./blacklist/IntegrationsBlackListedFabButton";
import integrationStyles from "./details/integrationStyles";
import Toggle from "../common/Toggle";
import appPages from "../../../appPages";
import DeleteIgnoreRuleDialog from "./delete/DeleteIgnoreRuleDialog";
import DeleteRuleConfirmDialog from "./delete/DeleteRuleConfirmDialog";
import IntegrationIcons from "../common/icons/integrations";

const { height: HEIGHT } = Dimensions.get("window");

const IGNORED_ELEMENT = 0;

export default class IntegrationRulesPage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;

    this.listRef = null;

    const params = this.props.route.params || {};
    const showAssociations = !params.tab || params.tab == "associations";

    this.state = {
      rules: [],
      showAssociations,
      returnUrl: this._getReturnUrl(props),
    };
  }

  // Got back to initial url and skip ignore flow
  _getReturnUrl = (props) => {
    const navState = props.navigation?.dangerouslyGetState() || {};
    const routes = (navState.routes.slice(-4) || []).reverse().slice(1); // skip current

    let returnUrl = "";
    routes.forEach((r) => {
      if (
        !returnUrl &&
        r.name !== appPages.integrationsIgnorePage &&
        r.name !== appPages.integrationsDeletePage
      ) {
        returnUrl = r.name;
      }
    });

    return returnUrl;
  };

  componentDidMount = async () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.initRules();
        },
      );
    } else {
      this.initRules();
    }
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  initRules = async () => {
    const allRules = await this.controller.getIntegrationRules();

    this.setState(
      (state) => ({
        ...state,
        allRules,
      }),
      () => {
        this._filterRulesByTab();
      },
    );
  };

  _filterRulesByTab = () => {
    const { allRules, showAssociations } = this.state;

    const params = this.props.route.params || {};
    const selectedIds = params.selectedIds;

    let rules = [];
    if (hasItems(allRules)) {
      rules = allRules.filter((r) =>
        showAssociations
          ? r.id_element != IGNORED_ELEMENT
          : r.id_element == IGNORED_ELEMENT,
      );

      if (hasItems(selectedIds)) {
        rules.forEach((int) => {
          int.isExpanded = selectedIds.includes(int.id);
        });
      }
    }

    this.setState((state) => ({
      ...state,
      rules,
      filteredRules: rules,
      selectedIds,
    }));
  };

  onContentSizeChange = () => {
    const { filteredRules, selectedIds } = this.state;

    if (!hasItems(filteredRules) || !hasItems(selectedIds)) return;

    let scrollIndex = -1;
    filteredRules.forEach((r, index) => {
      if (selectedIds.includes(r.id)) {
        scrollIndex = index;
      }
    });

    // Scroll
    if (scrollIndex > -1) {
      this.listRef.scrollToIndex({ index: scrollIndex, animated: false });
    }
  };

  goBack = () => {
    const { returnUrl } = this.state;

    if (returnUrl) {
      this.controller.navigate(returnUrl);
    } else {
      this.controller.goBack();
    }
  };

  // ------------------------------------- FILTER -------------------------------------
  filterByKeyword = ({ term }) => {
    const { rules } = this.state;

    const pattern = Utils.getSearchPattern(term);
    const filteredRules = term
      ? rules.filter((a) => {
          return a.name && a.name.match(pattern);
        })
      : rules;

    this.setState((state) => ({
      ...state,
      isLoading: false,
      filteredRules,
    }));
  };

  // ------------------------------------- DIALOG -------------------------------------
  toggleDeleteRuleDialog = (selectedRule) => {
    this.setState((state) => ({
      ...state,
      selectedRule,
    }));
  };

  deleteRule = () => {
    const { selectedRule, rules } = this.state;
    if (!selectedRule) return;

    IntegrationsHelper.deleteRule(this.controller, {
      id: selectedRule.matching_rule,
    }).then(() => {
      this.toggleDeleteRuleDialog(null);

      const remaining = rules.filter((r) => r.id != selectedRule.matching_rule);

      this.setState((state) => ({
        ...state,
        rules: remaining,
        filteredRules: remaining,
      }));
    });
  };

  associate = (selectedRule) => {
    IntegrationsHelper.deleteRule(this.controller, selectedRule.rule).then(
      () => {
        this.toggleDeleteRuleDialog(null);
        this.goToMappingPage(selectedRule);
      },
    );
  };

  // ------------------------------------- TAB -------------------------------------
  toggleSelectedTab = () => {
    const { showAssociations } = this.state;

    this.setState(
      (state) => ({
        ...state,
        showAssociations: !showAssociations,
      }),
      () => {
        this._filterRulesByTab();
      },
    );
  };

  goToIgnorePage = () => {
    const { selectedRule } = this.state;

    this.toggleDeleteRuleDialog(null);
    navigate(this.controller, appPages.integrationsIgnorePage, "changePage", {
      rules: [selectedRule],
    });
  };

  goToMappingPage = (integration) => {
    navigate(this.controller, appPages.integrationsMappingPage, "changePage", {
      integrations: [integration],
      returnUrl: appPages.integrationRulesPage,
    });
  };

  goToMappingGame = () => {
    navigate(this.controller, appPages.integrationsMappingGame);
  };

  // ------------------------------------- RENDER -------------------------------------
  render() {
    const {
      filteredRules,
      isLoading,
      selectedRule,
      selectedIds,
      showAssociations,
    } = this.state;

    const hasSelected = hasItems(selectedIds);
    const DELETE_DIALOG = showAssociations
      ? DeleteRuleConfirmDialog
      : DeleteIgnoreRuleDialog;

    return (
      <View style={[appStyles.container, { backgroundColor: "#F3F2F1" }]}>
        <KeyboardAvoidingView
          style={
            hasSelected
              ? [appStyles.kbView, { height: HEIGHT - 100 }]
              : appStyles.kbViewNoFooter
          }
          behavior="position"
          keyboardVerticalOffset={isAndroid ? 30 : 0}
        >
          <Header
            style={{ minHeight: 105 }}
            title="Integration Rules"
            subTitle="Your rules for handling integration data"
            isLoading={isLoading}
            goBack={this.goBack}
            iconTag={
              <IntegrationIcons.IntegrationsIconWhite
                onPress={this.goToMappingGame}
              />
            }
          >
            <Toggle
              selected={showAssociations ? "Associations" : "To Ignore"}
              options={["Associations", "To Ignore"]}
              onToggle={this.toggleSelectedTab}
              style={{
                marginHorizontal: 18,
              }}
            />
          </Header>

          {/* Integrations */}
          {hasItems(filteredRules) ? (
            <FlatList
              ref={(el) => (this.listRef = el)}
              onContentSizeChange={this.onContentSizeChange}
              getItemLayout={(_, index) => ({
                offset: 40 * index,
                length: 40,
                index,
              })}
              contentContainerStyle={{ paddingBottom: 100 }}
              style={integrationStyles.flatList}
              data={filteredRules}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => (
                <RuleWrapper
                  controller={this.controller}
                  data={item}
                  hasDropdownArrow
                  hasRules
                  showDate
                  showRulesSummary
                  showSelector={filteredRules.length > 1}
                  showAssociation={showAssociations}
                  //isReadOnly={!showAssociations}
                  style={{ paddingRight: 0 }}
                  deleteRule={this.toggleDeleteRuleDialog}
                  changeAssociation={this.goToMappingPage}
                />
              )}
            />
          ) : null}

          <View style={[appStyles.footerOptions]}>
            <Search
              showIcon={false}
              controller={this.controller}
              placeholder="Search Data"
              filterResults={this.filterByKeyword}
              autoFocus={false}
            />
          </View>
        </KeyboardAvoidingView>
        <View style={[appStyles.footerOptions]}>
          <IntegrationsBlackListedFabButton
            style={{ bottom: hasSelected ? 0 : 50 }}
            mariOptions={
              hasSelected
                ? {
                    highlight: true,
                    showTutorials: true,
                    mariMessage: {
                      message: "Your data is secure!",
                      description:
                        "We won’t use this kind of data to make predictions  about your activity anymore",
                    },
                  }
                : null
            }
          />
        </View>

        {/* Delete rule confirm dialog */}
        {selectedRule ? (
          <DELETE_DIALOG
            controller={this.controller}
            integration={selectedRule}
            data={[selectedRule]}
            cancel={() => this.toggleDeleteRuleDialog(null)}
            confirm={this.deleteRule}
            associate={this.associate}
            ignore={this.goToIgnorePage}
          />
        ) : null}
      </View>
    );
  }
}
